import React, { useEffect, useRef, useState } from 'react';
import { useUser } from '../../../Context/useUser';
import API from '../../../Utils/api';
import Table from '../../../Components/Table/Table';
import TimeOfDayInput from '../../../Components/TimeOfDayInput/TimeOfDayInput';
import styles from './GroupPage.module.css';
import Modal from '../../../Components/Modal/Modal';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useParams } from 'react-router';
import {
    MdAdd,
    MdPersonAdd,
    MdPersonRemove,
    MdEdit,
    MdDelete,
    MdPeople,
    MdCalendarToday,
    MdFactCheck
} from 'react-icons/md';
import { Link } from 'react-router';
import { useForm } from 'react-hook-form';
import AttendanceSheet from './AttendanceSheet';
import { WEEKDAYS_MONDAY_FIRST, mondayFirstOffset } from './calendarUtils';

// Workspace views for the selected group, in tab order.
const VIEW_TABS = [
    { id: 'athletes', label: 'Athletes', title: 'View Athletes', Icon: MdPeople },
    { id: 'workouts', label: 'Workouts', title: 'View Workouts', Icon: MdCalendarToday },
    { id: 'attendance', label: 'Attendance', title: 'View Attendance', Icon: MdFactCheck }
];

const plural = (count, noun) => `${count} ${noun}${count === 1 ? '' : 's'}`;

const WorkoutCalendar = ({ workouts }) => {
    const [currentMonth, setCurrentMonth] = useState(new Date());

    const daysInMonth = date => {
        return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    };

    const workoutsByDate = workouts.reduce((acc, workout) => {
        const date = new Date(workout.scheduledDate).toDateString();
        if (!acc[date]) acc[date] = [];
        acc[date].push(workout);
        return acc;
    }, {});

    const renderCalendar = () => {
        const days = daysInMonth(currentMonth);
        const firstDay = mondayFirstOffset(currentMonth);
        const calendarDays = [];

        // Empty cells for days before month starts
        for (let i = 0; i < firstDay; i++) {
            calendarDays.push(
                <div key={`empty-${i}`} className={styles.calendarDay + ' ' + styles.empty}></div>
            );
        }

        // Days of the month
        for (let day = 1; day <= days; day++) {
            const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
            const dateStr = date.toDateString();
            const dayWorkouts = workoutsByDate[dateStr] || [];
            const isToday = new Date().toDateString() === dateStr;

            calendarDays.push(
                <div
                    key={day}
                    className={`${styles.calendarDay} ${isToday ? styles.today : ''} ${dayWorkouts.length > 0 ? styles.hasWorkouts : ''}`}
                    title={dayWorkouts.length > 0 ? `${dayWorkouts.length} workout(s)` : ''}
                >
                    <div className={styles.dayNumber}>{day}</div>
                    {dayWorkouts.length > 0 && (
                        <div className={styles.workoutIndicator}>{dayWorkouts.length}</div>
                    )}
                </div>
            );
        }

        return calendarDays;
    };

    const previousMonth = () => {
        setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
    };

    const nextMonth = () => {
        setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
    };

    const monthName = currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

    return (
        <div className={styles.calendar}>
            <div className={styles.calendarHeader}>
                <button onClick={previousMonth} className={styles.calendarNav}>
                    ‹
                </button>
                <h3>{monthName}</h3>
                <button onClick={nextMonth} className={styles.calendarNav}>
                    ›
                </button>
            </div>
            <div className={styles.calendarWeekdays}>
                {WEEKDAYS_MONDAY_FIRST.map(day => (
                    <div key={day} className={styles.weekday}>
                        {day}
                    </div>
                ))}
            </div>
            <div className={styles.calendarGrid}>{renderCalendar()}</div>
        </div>
    );
};

const CreateGroupModal = ({ onClose, onSubmit, submitting }) => {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const inputRef = useRef(null);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, []);

    const handleSubmit = e => {
        e.preventDefault();
        const trimmedName = name.trim();
        if (!trimmedName) {
            toast.error('Group name is required');
            return;
        }
        onSubmit?.({
            name: trimmedName,
            description: description.trim()
        });
    };

    return (
        <Modal closeCB={onClose} title='Create Group'>
            <form onSubmit={handleSubmit} className={styles.createForm}>
                <input
                    ref={inputRef}
                    type='text'
                    placeholder='Group Name'
                    value={name}
                    onChange={e => setName(e.target.value)}
                    required
                />
                <input
                    type='text'
                    placeholder='Description'
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                />
                <button type='submit' disabled={submitting}>
                    {submitting ? 'Creating...' : 'Create Group'}
                </button>
            </form>
        </Modal>
    );
};

const RenameGroupModal = ({ group, onClose, onSubmit, submitting }) => {
    const [name, setName] = useState(group?.name || '');
    const [description, setDescription] = useState(group?.description || '');
    const inputRef = useRef(null);

    useEffect(() => {
        setName(group?.name || '');
        setDescription(group?.description || '');
    }, [group]);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, [group]);

    const handleSubmit = e => {
        e.preventDefault();
        const trimmedName = name.trim();
        const payload = {
            name: trimmedName,
            description: (description || '').trim()
        };
        onSubmit?.(payload);
    };

    return (
        <Modal closeCB={onClose} title='Rename Group'>
            <form onSubmit={handleSubmit} className={styles.createForm}>
                <input
                    ref={inputRef}
                    type='text'
                    placeholder='Group Name'
                    value={name}
                    onChange={e => setName(e.target.value)}
                    required
                />
                <input
                    type='text'
                    placeholder='Description'
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                />
                <button type='submit' disabled={submitting}>
                    {submitting ? 'Renaming...' : 'Rename Group'}
                </button>
            </form>
        </Modal>
    );
};

export default function GroupPage() {
    const { user } = useUser();
    const { coachId } = useParams();
    const isAdminVisiting = user?.type === 'Admin' && coachId;
    const userIdToUse = isAdminVisiting ? coachId : user?._id;
    const [groups, setGroups] = useState([]);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [athletes, setAthletes] = useState([]);
    const [creating, setCreating] = useState(false);
    const [allAthletes, setAllAthletes] = useState([]);
    const [showAddModal, setShowAddModal] = useState(false);
    const [addingAthleteId, setAddingAthleteId] = useState(null);
    const [removingAthleteId, setRemovingAthleteId] = useState(null);
    const [confirmRemove, setConfirmRemove] = useState(null);
    const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
    const [openGroupMenu, setOpenGroupMenu] = useState(null);
    const [confirmDeleteGroup, setConfirmDeleteGroup] = useState(null);
    const [showRenameModal, setShowRenameModal] = useState(false);
    const [renamingGroup, setRenamingGroup] = useState(null);
    const [renaming, setRenaming] = useState(false);
    const [futureWorkouts, setFutureWorkouts] = useState([]);
    const [pastWorkouts, setPastWorkouts] = useState([]);
    const [showCreateProgramGroupModal, setShowCreateProgramGroupModal] = useState(false);
    const [programs, setPrograms] = useState([]);
    const [editingWorkout, setEditingWorkout] = useState(null);
    const [showEditDateModal, setShowEditDateModal] = useState(false);
    const [confirmDeleteWorkout, setConfirmDeleteWorkout] = useState(null);
    const [attendanceData, setAttendanceData] = useState(null);
    const [viewMode, setViewMode] = useState('athletes'); // 'athletes', 'attendance', or 'workouts'
    const tabRefs = useRef({});

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors, isSubmitting }
    } = useForm({
        defaultValues: {
            program: '',
            scheduledDate: ''
        }
    });

    const {
        register: registerEdit,
        handleSubmit: handleSubmitEdit,
        reset: resetEdit,
        formState: { errors: editErrors, isSubmitting: isEditSubmitting }
    } = useForm();

    useEffect(() => {
        const loadGroups = async () => {
            if (!userIdToUse) return;
            try {
                const { data } = await API.getMyGroups(userIdToUse);
                setGroups(data);
            } catch (error) {
                toast.error('Failed to load groups');
            }
        };
        loadGroups();
    }, [userIdToUse]);

    useEffect(() => {
        const loadAthletes = async () => {
            if (!selectedGroup || !userIdToUse) return;
            try {
                const { data } = await API.getAthletesById(userIdToUse);
                setAllAthletes(data.map(r => r.athlete));
            } catch (err) {
                setAllAthletes([]);
            }
        };
        loadAthletes();
    }, [selectedGroup, userIdToUse]);

    useEffect(() => {
        const loadGroupAthletes = async () => {
            if (!selectedGroup) return;
            try {
                const { data } = await API.getAthletesForGroup(selectedGroup._id);
                setAthletes(
                    data.map(rel => ({
                        ...rel.athleteId,
                        lastWorkout: rel.lastWorkout
                    }))
                );
            } catch (err) {
                setAthletes([]);
            }
        };
        loadGroupAthletes();
    }, [selectedGroup]);

    useEffect(() => {
        const loadWorkouts = async () => {
            if (!selectedGroup) return;
            try {
                const [futureRes, pastRes] = await Promise.all([
                    API.getFutureProgramGroupsByGroup(selectedGroup._id),
                    API.getPastProgramGroupsByGroup(selectedGroup._id)
                ]);
                setFutureWorkouts(futureRes.data);
                setPastWorkouts(pastRes.data);
            } catch (error) {
                toast.error('Failed to load workouts');
            }
        };
        loadWorkouts();
    }, [selectedGroup]);

    useEffect(() => {
        const loadPrograms = async () => {
            try {
                const response = await API.getMyPrograms(userIdToUse);
                setPrograms(response.data);
            } catch (error) {
                toast.error('Failed to load programs');
            }
        };

        if (userIdToUse) {
            loadPrograms();
        }
    }, [userIdToUse]);

    useEffect(() => {
        if (!selectedGroup) return;
        const fetchGroupData = async () => {
            try {
                const { data: attendanceResponse } = await API.getGroupAttendance(
                    selectedGroup._id
                );
                setAttendanceData(attendanceResponse);
            } catch (err) {
                setAttendanceData({
                    programGroups: [],
                    athletes: []
                });
            }
        };

        fetchGroupData();
    }, [selectedGroup]);

    const handleCreateGroup = async payload => {
        if (!payload?.name) {
            toast.error('Group name is required');
            return;
        }
        setCreating(true);
        try {
            const body = {
                name: payload.name,
                description: payload.description || undefined,
                coach: userIdToUse
            };
            const { data } = await API.createGroup(body);
            setGroups(prev => [...prev, data]);
            setShowCreateGroupModal(false);
            toast.success('Group created successfully');
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Failed to create group');
        } finally {
            setCreating(false);
        }
    };

    const handleDeleteGroup = groupId => {
        setConfirmDeleteGroup(groupId);
    };

    const confirmDeleteGroupAction = async () => {
        if (!confirmDeleteGroup) return;
        try {
            await API.deleteGroup(confirmDeleteGroup);
            setGroups(prev => prev.filter(g => g._id !== confirmDeleteGroup));
            toast.success('Group deleted successfully');
        } catch (error) {
            toast.error('Failed to delete group');
        } finally {
            setConfirmDeleteGroup(null);
            setOpenGroupMenu(null);
        }
    };

    const handleAddAthleteToGroup = async athleteId => {
        if (athletes.some(a => a._id === athleteId)) {
            toast.error('Athlete is already in the group.');
            return;
        }
        setAddingAthleteId(athleteId);
        try {
            await API.addAthleteToGroup(selectedGroup._id, athleteId);
            const { data } = await API.getAthletesForGroup(selectedGroup._id);
            setAthletes(
                data.map(rel => ({
                    ...rel.athleteId,
                    lastWorkout: rel.lastWorkout
                }))
            );

            toast.success('Athlete added successfully');
        } catch (error) {
            toast.error('Failed to add athlete to group');
        } finally {
            setAddingAthleteId(null);
        }
    };

    const handleRemoveAthleteFromGroup = athlete => {
        setConfirmRemove(athlete);
    };

    const confirmRemoveAthlete = async () => {
        if (!confirmRemove) return;
        setRemovingAthleteId(confirmRemove._id);
        try {
            await API.removeAthleteFromGroup(selectedGroup._id, confirmRemove._id);
            const { data } = await API.getAthletesForGroup(selectedGroup._id);
            setAthletes(
                data.map(rel => ({
                    ...rel.athleteId,
                    lastWorkout: rel.lastWorkout
                }))
            );
            toast.success('Athlete removed successfully');
        } catch (error) {
            toast.error('Failed to remove athlete from group');
        } finally {
            setRemovingAthleteId(null);
            setConfirmRemove(null);
        }
    };

    const handleRenameGroup = async updatedData => {
        if (!renamingGroup) return;
        if (!updatedData?.name) {
            toast.error('Group name is required');
            return;
        }
        setRenaming(true);
        try {
            const trimmedDescription =
                updatedData.description && updatedData.description.length > 0
                    ? updatedData.description
                    : undefined;
            const payload = {
                name: updatedData.name,
                ...(trimmedDescription ? { description: trimmedDescription } : {})
            };
            const { data } = await API.updateGroup(renamingGroup._id, payload);
            setGroups(prev => prev.map(g => (g._id === data._id ? data : g)));
            setShowRenameModal(false);
            setRenamingGroup(null);
            toast.success('Group renamed successfully');
        } catch (error) {
            console.error('Failed to rename group:', error?.response?.data || error);
            toast.error(
                error?.response?.data?.message ||
                    error?.response?.data?.error ||
                    error?.message ||
                    'Failed to rename group'
            );
        } finally {
            setRenaming(false);
        }
    };

    const onSubmit = async data => {
        try {
            // Combine date and time into a single DateTime
            const scheduledDateTime = new Date(`${data.scheduledDate}T${data.scheduledTime}`);

            await API.createProgramGroup(selectedGroup._id, {
                program: data.program,
                scheduledDate: scheduledDateTime,
                status: 'Scheduled'
            });

            toast.success('Program group created successfully');
            setShowCreateProgramGroupModal(false);
            reset();

            // Refresh workouts
            const [futureResponse, pastResponse] = await Promise.all([
                API.getFutureProgramGroupsByGroup(selectedGroup._id),
                API.getPastProgramGroupsByGroup(selectedGroup._id)
            ]);
            setFutureWorkouts(futureResponse.data);
            setPastWorkouts(pastResponse.data);
        } catch (error) {
            console.error('Error creating program group:', error);
            toast.error(error.response?.data?.message || 'Failed to create program group');
        }
    };

    const handleUpdateWorkoutDate = async data => {
        try {
            // Combine date and time into a single DateTime
            const scheduledDateTime = new Date(`${data.scheduledDate}T${data.scheduledTime}`);

            await API.updateProgramGroup(editingWorkout._id, {
                ...editingWorkout,
                scheduledDate: scheduledDateTime
            });

            toast.success('Workout date and time updated successfully');
            setShowEditDateModal(false);
            setEditingWorkout(null);
            resetEdit();

            const [futureResponse, pastResponse] = await Promise.all([
                API.getFutureProgramGroupsByGroup(selectedGroup._id),
                API.getPastProgramGroupsByGroup(selectedGroup._id)
            ]);
            setFutureWorkouts(futureResponse.data);
            setPastWorkouts(pastResponse.data);
        } catch (error) {
            console.error('Error updating workout date:', error);
            toast.error(error.response?.data?.message || 'Failed to update workout date');
        }
    };

    const handleDeleteWorkout = async () => {
        if (!confirmDeleteWorkout) return;

        try {
            await API.deleteProgramGroup(confirmDeleteWorkout._id);
            toast.success('Workout deleted successfully');
            setConfirmDeleteWorkout(null);

            // Refresh workouts
            const [futureResponse, pastResponse] = await Promise.all([
                API.getFutureProgramGroupsByGroup(selectedGroup._id),
                API.getPastProgramGroupsByGroup(selectedGroup._id)
            ]);
            setFutureWorkouts(futureResponse.data);
            setPastWorkouts(pastResponse.data);
        } catch (error) {
            console.error('Error deleting workout:', error);
            toast.error(error.response?.data?.message || 'Failed to delete workout');
        }
    };

    const handleAttendanceUpdate = async () => {
        if (!selectedGroup) return;

        try {
            const { data: attendanceResponse } = await API.getGroupAttendance(selectedGroup._id);
            setAttendanceData(attendanceResponse);
        } catch (err) {
            toast.error('Failed to refresh attendance data');
        }
    };

    const athleteColumns = [
        {
            Header: 'Athlete Name',
            accessor: row => `${row.firstName} ${row.lastName}`,
            Cell: ({ row }) => (
                <Link to={`/athlete/${row.original._id}/profile`} className={styles.athleteLink}>
                    {row.original.firstName} {row.original.lastName}
                </Link>
            )
        },
        {
            id: 'age',
            Header: () => <div className={styles.cellCenter}>Age</div>,
            accessor: 'birthYear',
            Cell: ({ value }) => (
                <div className={styles.cellCenter}>
                    {value ? new Date().getFullYear() - value : '-'}
                </div>
            )
        },
        {
            id: 'gender',
            Header: () => <div className={styles.cellCenter}>Gender</div>,
            accessor: 'gender',
            Cell: ({ value }) => <div className={styles.cellCenter}>{value || '-'}</div>
        },
        {
            id: 'currentCSS',
            Header: () => <div className={styles.cellCenter}>Current CSS</div>,
            accessor: 'currentCSS',
            Cell: ({ value }) => (
                <div className={styles.cellCenter}>{value ? Number(value).toFixed(2) : '-'}</div>
            )
        },
        {
            id: 'lastWorkoutDate',
            Header: () => <div className={styles.cellCenter}>Last Workout Date</div>,
            accessor: 'lastWorkout',
            Cell: ({ value }) => (
                <div className={styles.cellCenter}>
                    {value ? new Date(value).toLocaleDateString('el-GR') : '-'}
                </div>
            )
        },
        {
            Header: () => <div className={styles.cellEnd}>Actions</div>,
            accessor: 'actions',
            Cell: ({ row }) => (
                <div className={styles.actionButtons}>
                    <button
                        type='button'
                        className={`${styles.actionBtn} ${styles.deleteBtn}`}
                        onClick={e => {
                            e.stopPropagation();
                            handleRemoveAthleteFromGroup(row.original);
                        }}
                        disabled={removingAthleteId === row.original._id}
                        title='Remove athlete'
                        aria-label={`Remove ${row.original.firstName} ${row.original.lastName} from group`}
                    >
                        {removingAthleteId === row.original._id ? (
                            '...'
                        ) : (
                            <MdPersonRemove size={18} aria-hidden='true' />
                        )}
                    </button>
                </div>
            )
        }
    ];

    const availableAthletes = allAthletes.filter(
        a => !athletes.some(inGroup => String(inGroup._id) === String(a._id))
    );

    const workoutColumns = [
        {
            Header: 'Program Name',
            accessor: row => row.program?.name || '-'
        },
        {
            Header: 'Scheduled Date & Time',
            accessor: 'scheduledDate',
            Cell: ({ value }) => {
                const date = new Date(value);
                const dateStr = date.toLocaleDateString('el-GR');
                const timeStr = date.toLocaleTimeString('en-GB', {
                    hour: '2-digit',
                    minute: '2-digit'
                });
                return `${dateStr} ${timeStr}`;
            }
        },
        {
            Header: () => <div className={styles.cellEnd}>Actions</div>,
            accessor: 'actions',
            Cell: ({ row }) => {
                const workout = row.original;
                const isUpcoming = new Date(workout.scheduledDate) > new Date();
                const label = `${workout.program?.name || 'workout'} on ${new Date(
                    workout.scheduledDate
                ).toLocaleDateString('el-GR')}`;

                return (
                    <div className={styles.actionButtons}>
                        {isUpcoming && (
                            <button
                                type='button'
                                className={styles.actionBtn}
                                onClick={() => {
                                    setEditingWorkout(workout);
                                    setShowEditDateModal(true);
                                }}
                                title='Edit date'
                                aria-label={`Edit date of ${label}`}
                            >
                                <MdEdit size={18} aria-hidden='true' />
                            </button>
                        )}
                        <button
                            type='button'
                            className={`${styles.actionBtn} ${styles.deleteBtn}`}
                            onClick={() => setConfirmDeleteWorkout(workout)}
                            title='Delete workout'
                            aria-label={`Delete ${label}`}
                        >
                            <MdDelete size={18} aria-hidden='true' />
                        </button>
                    </div>
                );
            }
        }
    ];

    const handleTabKeyDown = (e, index) => {
        const targets = {
            ArrowRight: index + 1,
            ArrowLeft: index - 1,
            Home: 0,
            End: VIEW_TABS.length - 1
        };
        if (!(e.key in targets)) return;
        e.preventDefault();
        const next = VIEW_TABS[(targets[e.key] + VIEW_TABS.length) % VIEW_TABS.length];
        setViewMode(next.id);
        tabRefs.current[next.id]?.focus();
    };

    if (!user || (user.type !== 'Coach' && user.type !== 'Admin')) {
        return <div className={styles.pageWrap}>You do not have access to this page.</div>;
    }

    return (
        <div className={styles.groupPageLayout}>
            <div className={styles.groupsList + ' island'}>
                <div className={styles.groupsHeader}>
                    <h2>Groups</h2>
                    <button
                        className={styles.createGroupBtn}
                        title='Create Group'
                        onClick={() => setShowCreateGroupModal(true)}
                    >
                        <MdAdd size={24} />
                    </button>
                </div>
                {groups.length === 0 && <div className={styles.emptyState}>No groups found.</div>}
                <div>
                    {groups.map(group => {
                        const isSelected = !!selectedGroup && selectedGroup._id === group._id;
                        return (
                            <div
                                key={group._id}
                                className={styles.groupRow + (isSelected ? ' ' + styles.selected : '')}
                                onMouseLeave={() => setOpenGroupMenu(null)}
                            >
                                <button
                                    type='button'
                                    className={styles.groupSelect}
                                    aria-pressed={isSelected}
                                    onClick={() => setSelectedGroup(group)}
                                >
                                    <span className={styles.groupTitle}>{group.name}</span>
                                    <span className={styles.groupDesc}>{group.description}</span>
                                    <span className={styles.groupCreated}>
                                        Created: {new Date(group.createdAt).toLocaleDateString()}
                                    </span>
                                </button>
                                <div className={styles.groupActions}>
                                    <button
                                        className={styles.dotsBtn}
                                        onClick={e => {
                                            e.stopPropagation();
                                            setOpenGroupMenu(
                                                openGroupMenu === group._id ? null : group._id
                                            );
                                        }}
                                    >
                                        &#8942;
                                    </button>
                                    {openGroupMenu === group._id && (
                                        <div className={styles.groupMenu}>
                                            <button
                                                className={styles.groupMenuItem}
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    setOpenGroupMenu(null);
                                                    setRenamingGroup(group);
                                                    setShowRenameModal(true);
                                                }}
                                            >
                                                Rename Group
                                            </button>
                                            <button
                                                className={styles.groupMenuItem}
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    setOpenGroupMenu(null);
                                                    handleDeleteGroup(group._id);
                                                }}
                                            >
                                                Delete Group
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
                {showCreateGroupModal && (
                    <CreateGroupModal
                        onClose={() => setShowCreateGroupModal(false)}
                        onSubmit={handleCreateGroup}
                        submitting={creating}
                    />
                )}
                {showRenameModal && renamingGroup && (
                    <RenameGroupModal
                        group={renamingGroup}
                        onClose={() => {
                            setShowRenameModal(false);
                            setRenamingGroup(null);
                        }}
                        onSubmit={handleRenameGroup}
                        submitting={renaming}
                    />
                )}
            </div>
            <div className={`${styles.athletesTableWrap} ${styles.workspace}`}>
                {selectedGroup ? (
                    <>
                        <div className={styles.viewToggle} role='tablist' aria-label='Group views'>
                            {VIEW_TABS.map(({ id, label, title, Icon }, index) => (
                                <button
                                    key={id}
                                    ref={el => (tabRefs.current[id] = el)}
                                    type='button'
                                    role='tab'
                                    id={`group-view-${id}-tab`}
                                    aria-selected={viewMode === id}
                                    aria-controls={`group-view-${id}-panel`}
                                    tabIndex={viewMode === id ? 0 : -1}
                                    className={`${styles.toggleBtn} ${
                                        viewMode === id ? styles.active : ''
                                    }`}
                                    onClick={() => setViewMode(id)}
                                    onKeyDown={e => handleTabKeyDown(e, index)}
                                    title={title}
                                >
                                    <Icon size={18} aria-hidden='true' />
                                    <span>{label}</span>
                                </button>
                            ))}
                        </div>

                        <section
                            className={styles.panel}
                            role='tabpanel'
                            id={`group-view-${viewMode}-panel`}
                            aria-labelledby={`group-view-${viewMode}-tab`}
                        >
                            {viewMode === 'athletes' ? (
                                <>
                                    <header className={styles.panelHead}>
                                        <div className={styles.panelHeadText}>
                                            <h3 className={styles.panelTitle}>Athletes</h3>
                                            <p className={styles.panelSub}>
                                                {selectedGroup.name} · {plural(athletes.length, 'athlete')}
                                            </p>
                                        </div>
                                        <button
                                            type='button'
                                            className={styles.primaryBtn}
                                            onClick={() => {
                                                setShowAddModal(true);
                                            }}
                                            title='Add athlete to group'
                                        >
                                            <MdAdd size={18} aria-hidden='true' />
                                            <span>Add athlete</span>
                                        </button>
                                    </header>
                                    <div className={`${styles.panelBody} ${styles.tableContainer}`}>
                                        {athletes.length > 0 ? (
                                            <Table columns={athleteColumns} data={athletes} />
                                        ) : (
                                            <div className={styles.emptyState}>
                                                No athletes in this group yet. Use Add athlete to add
                                                athletes.
                                            </div>
                                        )}
                                    </div>
                                </>
                            ) : viewMode === 'workouts' ? (
                                <>
                                    <header className={styles.panelHead}>
                                        <div className={styles.panelHeadText}>
                                            <h3 className={styles.panelTitle}>Workouts</h3>
                                            <p className={styles.panelSub}>
                                                {futureWorkouts.length} upcoming · {pastWorkouts.length} past
                                            </p>
                                        </div>
                                        <button
                                            type='button'
                                            className={styles.primaryBtn}
                                            onClick={() => setShowCreateProgramGroupModal(true)}
                                            title='Add workout'
                                        >
                                            <MdAdd size={18} aria-hidden='true' />
                                            <span>Add workout</span>
                                        </button>
                                    </header>

                                    <div className={`${styles.panelBody} ${styles.workoutsBody}`}>
                                        <section
                                            className={`${styles.workoutsColumn} ${styles.calendarColumn}`}
                                            aria-labelledby='group-workout-calendar'
                                        >
                                            <div className={styles.subPanelHead}>
                                                <h4 id='group-workout-calendar' className={styles.subPanelTitle}>
                                                    Workout Calendar
                                                </h4>
                                            </div>
                                            <WorkoutCalendar workouts={[...futureWorkouts, ...pastWorkouts]} />
                                        </section>

                                        <div className={styles.workoutsGrid}>
                                            <section
                                                className={styles.workoutsColumn}
                                                aria-labelledby='group-upcoming-workouts'
                                            >
                                                <div className={styles.subPanelHead}>
                                                    <h4 id='group-upcoming-workouts' className={styles.subPanelTitle}>
                                                        Upcoming Workouts
                                                    </h4>
                                                    <span className={styles.countBadge}>
                                                        {futureWorkouts.length}
                                                    </span>
                                                </div>
                                                {futureWorkouts.length > 0 ? (
                                                    <Table
                                                        columns={workoutColumns}
                                                        data={futureWorkouts}
                                                        rowsPerPage={5}
                                                    />
                                                ) : (
                                                    <div className={styles.emptyState}>
                                                        No upcoming workouts
                                                    </div>
                                                )}
                                            </section>

                                            <section
                                                className={styles.workoutsColumn}
                                                aria-labelledby='group-past-workouts'
                                            >
                                                <div className={styles.subPanelHead}>
                                                    <h4 id='group-past-workouts' className={styles.subPanelTitle}>
                                                        Past Workouts
                                                    </h4>
                                                    <span className={styles.countBadge}>
                                                        {pastWorkouts.length}
                                                    </span>
                                                </div>
                                                {pastWorkouts.length > 0 ? (
                                                    <Table
                                                        columns={workoutColumns}
                                                        data={pastWorkouts}
                                                        rowsPerPage={5}
                                                    />
                                                ) : (
                                                    <div className={styles.emptyState}>
                                                        No past workouts
                                                    </div>
                                                )}
                                            </section>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <header className={styles.panelHead}>
                                        <div className={styles.panelHeadText}>
                                            <h3 className={styles.panelTitle}>Attendance</h3>
                                            <p className={styles.panelSub}>
                                                {selectedGroup.name} · select a status to update it
                                            </p>
                                        </div>
                                    </header>
                                    <div className={styles.panelBody}>
                                        {attendanceData ? (
                                            <AttendanceSheet
                                                programGroups={attendanceData.programGroups || []}
                                                athletes={attendanceData.athletes || []}
                                                onAttendanceUpdate={handleAttendanceUpdate}
                                            />
                                        ) : (
                                            <div className={styles.emptyState} role='status'>
                                                Loading attendance data...
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </section>
                        {showAddModal && (
                            <Modal
                                closeCB={() => setShowAddModal(false)}
                                title='Add Athlete to Group'
                            >
                                {availableAthletes.length === 0 ? (
                                    <div className={styles.emptyState}>
                                        No available athletes to add.
                                    </div>
                                ) : (
                                    <ul className={styles.athleteListModal}>
                                        {availableAthletes.map(a => (
                                            <li key={a._id} className={styles.athleteListItem}>
                                                {a.firstName} {a.lastName} ({a.email})
                                                <button
                                                    className={styles.addBtn}
                                                    onClick={() => handleAddAthleteToGroup(a._id)}
                                                    disabled={addingAthleteId === a._id}
                                                    title='Add to group'
                                                >
                                                    {addingAthleteId === a._id ? (
                                                        '...'
                                                    ) : (
                                                        <MdPersonAdd size={20} />
                                                    )}
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </Modal>
                        )}
                        {confirmRemove && (
                            <Modal closeCB={() => setConfirmRemove(null)} title='Remove Athlete'>
                                <div>
                                    Are you sure you want to remove{' '}
                                    <b>
                                        {confirmRemove.firstName} {confirmRemove.lastName}
                                    </b>{' '}
                                    from <b>{selectedGroup.name}</b>
                                </div>
                                <div className={styles.modalActionRow}>
                                    <button
                                        className={styles.modalDeleteBtn}
                                        onClick={confirmRemoveAthlete}
                                        disabled={removingAthleteId === confirmRemove._id}
                                        title='Remove'
                                    >
                                        {removingAthleteId === confirmRemove._id ? '...' : 'Remove'}
                                    </button>
                                </div>
                            </Modal>
                        )}
                    </>
                ) : (
                    <div className={styles.emptyState}>Select a group to view its athletes</div>
                )}
                {confirmDeleteGroup && (
                    <Modal closeCB={() => setConfirmDeleteGroup(null)} title='Delete Group'>
                        <div>Are you sure you want to delete this group?</div>
                        <div className={styles.modalActionRow}>
                            <button
                                className={styles.cancelBtn}
                                onClick={() => setConfirmDeleteGroup(null)}
                                title='Cancel'
                            >
                                Cancel
                            </button>
                            <button
                                className={`${styles.submitBtn} ${styles.deleteBtn}`}
                                onClick={confirmDeleteGroupAction}
                                title='Delete group'
                            >
                                Delete
                            </button>
                        </div>
                    </Modal>
                )}
            </div>

            {showCreateProgramGroupModal && (
                <Modal
                    closeCB={() => {
                        setShowCreateProgramGroupModal(false);
                        reset();
                    }}
                    title='Create New Workout'
                >
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className={styles.formGroup}>
                            <label>Program</label>
                            <select
                                {...register('program', {
                                    required: 'Program is required'
                                })}
                                className={errors.program ? styles.error : ''}
                            >
                                <option value=''>Select a program</option>
                                {programs.map(program => (
                                    <option key={program._id} value={program._id}>
                                        {program.name}
                                    </option>
                                ))}
                            </select>
                            {errors.program && (
                                <span className={styles.errorMessage}>
                                    {errors.program.message}
                                </span>
                            )}
                        </div>
                        <div className={styles.formGroup}>
                            <label>Date</label>
                            <input
                                type='date'
                                {...register('scheduledDate', {
                                    required: 'Date is required'
                                })}
                                className={errors.scheduledDate ? styles.error : ''}
                            />
                            {errors.scheduledDate && (
                                <span className={styles.errorMessage}>
                                    {errors.scheduledDate.message}
                                </span>
                            )}
                        </div>
                        <div className={styles.formGroup}>
                            <TimeOfDayInput
                                id='scheduledTime'
                                label='Time'
                                {...register('scheduledTime', {
                                    required: 'Time is required'
                                })}
                                className={errors.scheduledTime ? styles.error : ''}
                                defaultValue='09:00'
                            />
                            {errors.scheduledTime && (
                                <span className={styles.errorMessage}>
                                    {errors.scheduledTime.message}
                                </span>
                            )}
                        </div>
                        <div className={styles.modalActionRow}>
                            <button
                                type='button'
                                className={styles.cancelBtn}
                                onClick={() => {
                                    setShowCreateProgramGroupModal(false);
                                    reset();
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                type='submit'
                                className={styles.submitBtn}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? 'Creating...' : 'Create'}
                            </button>
                        </div>
                    </form>
                </Modal>
            )}

            {showEditDateModal && (
                <Modal
                    closeCB={() => {
                        setShowEditDateModal(false);
                        setEditingWorkout(null);
                        resetEdit();
                    }}
                    title='Edit Workout Date & Time'
                >
                    <form onSubmit={handleSubmitEdit(handleUpdateWorkoutDate)}>
                        <div className={styles.formGroup}>
                            <label>New Date</label>
                            <input
                                type='date'
                                {...registerEdit('scheduledDate', {
                                    required: 'Date is required',
                                    validate: value => {
                                        if (!value) return true;
                                        const selectedDate = new Date(value);
                                        const today = new Date();
                                        today.setHours(0, 0, 0, 0);
                                        return (
                                            selectedDate >= today ||
                                            'Date must be today or in the future'
                                        );
                                    }
                                })}
                                min={new Date().toISOString().split('T')[0]}
                                defaultValue={
                                    editingWorkout
                                        ? new Date(editingWorkout.scheduledDate)
                                              .toISOString()
                                              .split('T')[0]
                                        : ''
                                }
                                className={editErrors.scheduledDate ? styles.error : ''}
                            />
                            {editErrors.scheduledDate && (
                                <span className={styles.errorMessage}>
                                    {editErrors.scheduledDate.message}
                                </span>
                            )}
                        </div>
                        <div className={styles.formGroup}>
                            <TimeOfDayInput
                                id='editScheduledTime'
                                label='New Time'
                                {...registerEdit('scheduledTime', {
                                    required: 'Time is required'
                                })}
                                defaultValue={
                                    editingWorkout
                                        ? new Date(editingWorkout.scheduledDate)
                                              .toTimeString()
                                              .slice(0, 5)
                                        : '09:00'
                                }
                                className={editErrors.scheduledTime ? styles.error : ''}
                            />
                            {editErrors.scheduledTime && (
                                <span className={styles.errorMessage}>
                                    {editErrors.scheduledTime.message}
                                </span>
                            )}
                        </div>
                        <div className={styles.modalActionRow}>
                            <button
                                type='button'
                                className={styles.cancelBtn}
                                onClick={() => {
                                    setShowEditDateModal(false);
                                    setEditingWorkout(null);
                                    resetEdit();
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                type='submit'
                                className={styles.submitBtn}
                                disabled={isEditSubmitting}
                            >
                                {isEditSubmitting ? 'Updating...' : 'Update'}
                            </button>
                        </div>
                    </form>
                </Modal>
            )}

            {confirmDeleteWorkout && (
                <Modal closeCB={() => setConfirmDeleteWorkout(null)} title='Delete Workout'>
                    <div className={styles.confirmBody}>
                        <p>Are you sure you want to delete this workout?</p>
                        {new Date(confirmDeleteWorkout.scheduledDate) <= new Date() && (
                            <div className={styles.warningNote}>
                                <strong>Note:</strong> This will delete all completed workouts for
                                this program. Non-completed workouts will be kept and will not count
                                in attendance.
                            </div>
                        )}
                    </div>
                    <div className={styles.modalActionRow}>
                        <button
                            className={styles.cancelBtn}
                            onClick={() => setConfirmDeleteWorkout(null)}
                        >
                            Cancel
                        </button>
                        <button
                            className={`${styles.submitBtn} ${styles.deleteBtn}`}
                            onClick={handleDeleteWorkout}
                        >
                            Delete
                        </button>
                    </div>
                </Modal>
            )}
        </div>
    );
}
